import { ExternalLink } from 'lucide-react';
import { portalColors } from './portalStyles';

type PortalFooterVariant = 'crew' | 'quote' | 'invoice';

interface PortalFooterProps {
  variant: PortalFooterVariant;
  /**
   * Business name for interpolation into invoice/quote copy. Unused by crew variant.
   * Omitted on error-render branches (data not hydrated) — the primary line is
   * dropped in that case and the footer degrades to brand label + tagline.
   */
  businessName?: string;
  /**
   * Deep-link URL for the crew variant's "Open in CrewBit" CTA. Ignored by
   * invoice/quote variants (customer-facing pages don't cross-sell the app).
   */
  deepLinkUrl?: string | null;
}

export function PortalFooter({ variant, businessName, deepLinkUrl }: PortalFooterProps) {
  if (variant === 'crew') {
    return (
      <div style={wrapperStyle}>
        <div style={brandLabelStyle}>CrewBit</div>
        <div style={primaryStyle}>You're viewing your schedule on CrewBit.</div>
        <div style={secondaryStyle}>
          Get the full app to message your team, track hours, and manage your own clients.
        </div>

        {deepLinkUrl && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <a
              href={deepLinkUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={ctaStyle}
            >
              <ExternalLink size={16} strokeWidth={2} />
              Open in CrewBit
            </a>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 24 }}>
          <a
            href="https://apps.apple.com/ca/app/crew-bit/id6760967832"
            target="_blank"
            rel="noopener noreferrer"
            style={storeButtonStyle}
          >App Store</a>
          <a
            href="https://play.google.com/store/apps/details?id=ca.praetori.crewbit"
            target="_blank"
            rel="noopener noreferrer"
            style={storeButtonStyle}
          >Google Play</a>
        </div>

        <div style={taglineStyle}>
          Powered by CrewBit &middot; crewbit.ca
        </div>
      </div>
    );
  }

  const primaryCopy = businessName
    ? variant === 'invoice'
      ? `Pay securely via ${businessName}. Powered by CrewBit.`
      : `Review and approve this quote from ${businessName}.`
    : null;

  return (
    <div style={wrapperStyle}>
      <div style={brandLabelStyle}>CrewBit</div>
      {primaryCopy && (
        <div style={{ ...primaryStyle, marginBottom: 16 }}>{primaryCopy}</div>
      )}
      <div style={taglineStyle}>
        Powered by CrewBit &middot; crewbit.ca
      </div>
    </div>
  );
}

const wrapperStyle: React.CSSProperties = {
  marginTop: 48,
  paddingTop: 24,
  borderTop: `0.5px solid ${portalColors.divider}`,
  textAlign: 'center',
};

const brandLabelStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
  color: portalColors.muted,
  marginBottom: 8,
};

const primaryStyle: React.CSSProperties = {
  fontSize: 14,
  color: portalColors.muted,
  marginBottom: 4,
};

const secondaryStyle: React.CSSProperties = {
  fontSize: 14,
  color: portalColors.secondary,
  marginBottom: 16,
};

const ctaStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '12px 24px',
  borderRadius: 12,
  background: portalColors.primary,
  color: '#fff',
  fontSize: 14,
  fontWeight: 600,
  textDecoration: 'none',
};

const storeButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '10px 20px',
  borderRadius: 10,
  background: portalColors.primary,
  color: '#fff',
  fontSize: 13,
  fontWeight: 600,
  textDecoration: 'none',
};

const taglineStyle: React.CSSProperties = {
  fontSize: 11,
  color: portalColors.veryMuted,
};
